/**
 * PreBilling — the client record's Pre-billing tab body (native billing
 * v0; the full loop is the separate B1 design). The month's billing
 * review for one client: every posted note's billing facts + its invoice
 * line, INLINE-EDITABLE, with a HUMAN-CONFIRMED "Ready" gate (founder
 * directive 2026-07-07: nothing exports until a care manager marks it
 * ready — Ready is a human act, not a system inference), and a CSV the
 * biller takes into their existing invoicing.
 *
 * THE CONFIRM-EVERYTHING DISCIPLINE ON THE MONEY PATH. States (derived
 * server-side; rendered PLAINLY — R2, no engine words on screen):
 *   ready          human-confirmed (a person set the row to Ready). Shows
 *                  who marked it. INCLUDED in the CSV.
 *   needs_your_ok  eligible but not yet confirmed — the GOLD awaiting
 *                  state (design law: gold means "awaiting your confirm").
 *                  EXCLUDED from the CSV until confirmed. A 0h note
 *                  with its line lands here too and exports once
 *                  confirmed, its line opening "No charge.", so the
 *                  family sees the task done.
 *   needs_detail   no invoice line: sent back to its author, none drafted,
 *                  or only "No charge" (kickbackCopy.ts). Ineligible; no
 *                  Ready switch. Excluded from the CSV.
 *   still_reading / failed — the standing feed language; ineligible,
 *                  excluded.
 *
 * REMOVE NOTE. The edit box carries "Remove note"
 * where the Notes tab would offer Remove (canChangeNote: the author, or a
 * seat that moderates notes), behind the house confirm. It is the Notes
 * tab's own remove (POST /notes/{id}/void, notesApi.removeNote) and says
 * the server's refusal in the Notes tab's words (removeErrorMessage): a
 * note on an approved or issued invoice cannot be removed.
 *
 * THE OFFICE'S SECOND PASS lives here: the office reviews the ready set
 * and can still inline-edit. An edit takes a note off Ready and back to
 * its author, who marks it Ready again. The office's backstop seats
 * (moderate_clinical_notes) can mark any row when a care manager is
 * away. The Notes feed's Mark as ready follows the same author rule; no
 * second persisted state.
 *
 * THE READY SWITCH. On a row the viewer may mark, one Not ready | Ready
 * switch (the shared SegmentedToggle) is the state and the control: Not
 * ready is the faded terracotta while it waits, Ready is the primary fill. It shows the
 * chosen value at once, keeps focus through the save and the refetch, and
 * sends nothing for the value already shown or while its row's request is
 * in flight. A row the viewer may not mark shows its chip instead; another
 * author's awaiting row keeps the "Waiting for the care manager" chip
 * beside the backstop's switch. Edit has its own column (LP-18).
 *
 * ONE ROW HEIGHT (LP-18). Every cell but Summary holds one line: the
 * Rate cell's band and After Hours mark share its first line and its
 * quiet second line (the price, Export only) keeps its height when empty;
 * the Status cell is the switch with "Marked by" or the Waiting chip
 * after it on the same line. A refused mark says why on a line of its
 * own under the row, so the row itself never grows.
 *
 * CAPABILITY: the tab opens for view_invoice or view_prebilling. Every
 * rule on it keys off one seat test, view_invoice (`officeSeat`):
 *   - the office seat (view_invoice) sees the full screen: rates, the
 *     native-billing marks, Go to invoicing, the Ready switch, Mark all
 *     ready and the CSV. Its Edit and Ready doors follow the note author
 *     rule (canChangeNote) with write_clinical_notes. A pure viewer
 *     (view_invoice, no write) sees read-only rows and no controls.
 *   - a seat without view_invoice (a care manager) receives the lines
 *     with no money from the server and never asks for the rates. On a
 *     note they wrote they edit the whole line, as the office does:
 *     date, hours, the rate bands they may pick (labels only, never a
 *     price), time of day and the invoice line, on the same billing and
 *     summary routes; and they set Ready only on a note they wrote. The
 *     server's author rule refuses them on anyone else's note.
 *   - the edit box is the whole line for the note's author and for the
 *     office seat; the invoice line alone only for any other seat that
 *     may change the note.
 *     The awaiting chip reads "Needs your OK" to the note's author and
 *     "Waiting for the care manager" to everyone else.
 *
 * THE SHARED ROW. PrebillingRow is exported: My time's billing view
 * (components/time/MyTimeBilling.tsx) draws the viewer's own notes with the
 * same row, the same switch and state line and the same line-only edit
 * box, with the client beside the date, hours as "1.5 h", and, below `sm`,
 * as one card per note (`client`, `layout`).
 *
 * The rows arrive newest first and render in that order. The CSV
 * serializes the ALREADY-FETCHED ready rows client-side, oldest first (no
 * second fetch — the tab's audited read is the one access). The bulk and
 * CSV buttons sit above the table, never below the list. `today` is
 * INJECTED (TodayContext discipline).
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { ArrowRight } from 'lucide-react';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { UnpostedDraftsWarning } from '@/components/notes/UnpostedDraftsWarning';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { FLD_LABEL } from '@/components/primitives/modalFormStyles';
import { AfterHoursToggle } from '@/components/time/AfterHoursToggle';
import { Chip, type ChipVariant } from '@/components/primitives/Chip';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { NativeBillingOffBanner } from '@/components/billing/NativeBillingOffBanner';
import { removeErrorMessage } from '@/components/notes/NotesFeed';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { DestructiveTextAction } from '@/components/workspace/TextAction';
import { PrebillingStatesLegend } from '@/components/clientRecord/PrebillingStatesLegend';
import { STATE_CHIP, WAITING_CHIP } from '@/components/clientRecord/prebillingStateChips';
import { MARKER_ONLY_HINT, insufficientContentMessage } from '@/lib/kickbackCopy';
import { hasInvoiceLine, isInCapitals } from '@/lib/invoiceLinePresence';
import { proseDate, rowDate } from '@/lib/proseDate';
import { formatHoursTenths } from '@/lib/hours';
import {
  ADMIN_RATE_BAND,
  NOT_THE_AUTHOR_EDIT_LINE,
  PAY_PERIOD_CLOSED_EDIT_LINE,
  editorIsNotAuthor,
  noteDateRefusal,
  isPerVisitBand,
  notesApi,
  readRateBands,
  serverMessageFrom,
} from '@/lib/notes';
import { practiceTodayIso } from '@/lib/practiceDate';
import {
  contactSentence,
  errorCodeOf,
  errorText,
  markReadyKeyOf,
  pageWriteText,
  refusalText,
  type SupportContact,
} from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  hoursIncrementMessage,
  isOnHoursIncrement,
  readHoursIncrement,
} from '@/lib/hoursIncrement';
import { bandOptionsForWhoami } from '@/lib/bandVisibility';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { DateInput } from '@/components/primitives/DateInput';
import { useTenant } from '@/lib/auth';
import {
  canChangeNote,
  canViewInvoice,
  canWriteClinicalNotes,
} from '@/lib/capabilities';
import { ApiError } from '@/lib/api';
import { downloadCsvFile } from '@/lib/csv';
import { bandRatesOf, formatMoney, useClientRates } from '@/lib/clientRates';
import { invalidateMyHours } from '@/lib/timeEntriesApi';
import { invalidateMyPrebilling } from '@/lib/myPrebilling';
import {
  CAPITALS_WARNING,
  bandLabelsFrom,
  buildPrebillingCsv,
  formatHours,
  markReadyOutcomeOf,
  monthLabel,
  monthName,
  nativeExportOnlyExplanation,
  nextMonth,
  noChargeHoursWarningFor,
  patchNoteBilling,
  patchNoteSummary,
  prebillingCsvFilename,
  prevMonth,
  prebillingKey,
  rateBandLabel,
  readyHoursByBand,
  readyRowsOf,
  setNoteBillingReady,
  usePrebilling,
  type BillingPatch,
  type PrebillingNote,
} from '@/lib/prebilling';
import { SelectField } from '@/components/primitives/SelectField';

/** What an office edit of a Ready note on a draft invoice did when the
 *  note's new date left it billed by export: the save's answer names the
 *  outcome, and the row says it in plain words. Any other answer says
 *  nothing (the draft took the charge back). */
function editLeftInvoiceSentence(body: unknown): string | null {
  const outcome = markReadyOutcomeOf(body);
  if (outcome === 'before_native_start') {
    return "Saved, and because the new date is before the billing start date, it's off this draft invoice. Change the date back in Edit if that's wrong.";
  }
  if (outcome === 'native_billing_off') {
    return "Saved, and this note is off the draft invoice because invoices aren't made here yet. The office bills it the old way, so there's nothing more to do.";
  }
  return null;
}

/** The awaiting chip names whose OK it waits for: the author's own row
 *  reads "Needs your OK"; anyone else's reads "Waiting for the care
 *  manager", in the same gold. Every other state reads the same to
 *  everyone. It keys on authorship, never on the seat. The chips live in
 *  prebillingStateChips.ts, which the states popover reads too. */
export function chipFor(note: Pick<PrebillingNote, 'readiness'>, viewerIsAuthor: boolean): { label: string; variant: ChipVariant } {
  return note.readiness === 'needs_your_ok' && !viewerIsAuthor ? WAITING_CHIP : STATE_CHIP[note.readiness];
}

/** A seat without view_invoice reads a missing rate as something the
 *  office sets: no figure, and no door that seat lacks. */
export const NO_RATE_ASK_OFFICE = "This client has no rate yet, so this note isn't on the invoice. Ask the office to set it.";

/** A refused mark in plain words, the sentence that shows under the row.
 *  A seat without view_invoice (a care manager, and My time for every
 *  seat) reads the missing rate as something the office sets, and any
 *  other refusal as the server's sentence. The office reads the rate
 *  gate's fix on this tab: a missing band named as a band, a missing
 *  rate as a rate, missing hours as hours. On the all-client page
 *  (`place` 'all') the fix leads, and Edit is in the same row; a missing
 *  rate there is the page's own line (missingRateBandOf), naming the band
 *  and who can add it. */
export function readyRefusalMessage(
  err: unknown,
  officeSeat: boolean,
  contact: SupportContact,
  place: 'tab' | 'all' = 'tab',
): string {
  if (!officeSeat) {
    const key = markReadyKeyOf(err);
    return key === 'rate_not_on_file'
      ? NO_RATE_ASK_OFFICE
      : (serverMessageFrom(err) ?? refusalText(err, contact) ?? errorText('note_mark_ready', key, contact));
  }
  if (err instanceof ApiError && err.status === 409) {
    const code = (err.body as { error?: string } | null)?.error;
    if (code === 'rate_not_on_file') {
      const band = (err.body as { rate_band?: unknown } | null)?.rate_band;
      if (place === 'all') {
        return band === null
          ? 'Pick a rate band in Edit to bill this note.'
          : "Add a rate for this band to the client's Rates to bill this note.";
      }
      return band === null
        ? "This note has no rate band yet, so it isn't on the invoice yet. Pick one in Edit, then mark it Ready."
        : "No rate is on file for this band, so this note isn't on the invoice yet. Ask the office to set it in Rates above, then mark it Ready.";
    }
    if (code === 'hours_not_entered') {
      return place === 'all'
        ? 'Add the hours in Edit to bill this note.'
        : "This note has no hours yet, so it isn't on the invoice yet. Add them in Edit, then mark it Ready.";
    }
  }
  return serverMessageFrom(err) ?? pageWriteText(err, contact);
}

/** The band a mark was refused for because the client has no rate for it
 *  (the rate gate's 409 naming a band), else null. A refusal naming no
 *  band is a missing band, not a missing rate. */
export function missingRateBandOf(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { error?: unknown; rate_band?: unknown } | null;
  return body?.error === 'rate_not_on_file' && typeof body.rate_band === 'string' && body.rate_band !== ''
    ? body.rate_band
    : null;
}

/** A refused mark that names something the line still needs (a band or
 *  its hours), where nothing has failed. */
export function refusalNeedsSetup(err: unknown): boolean {
  const key = markReadyKeyOf(err);
  return key === 'rate_not_on_file' || key === 'rate_band_missing' || key === 'hours_not_entered';
}

/** The edit box's one Save: the invoice line first (only when it
 *  changed), then the hours, rate, date and time of day. The two halves
 *  ride two endpoints on purpose (the billing PATCH's allowlist and the
 *  summary PATCH's are disjoint); a half that fails is named
 *  (CombinedSaveError), never papered over. Every page with the box
 *  saves through this. */
export async function saveBillingLine(
  tenantSlug: string,
  noteId: string,
  patch: BillingPatch,
  summary?: string,
): Promise<unknown> {
  if (summary !== undefined) {
    try {
      await patchNoteSummary(tenantSlug, noteId, summary);
    } catch (err) {
      throw new CombinedSaveError('line', false, err);
    }
  }
  try {
    return await patchNoteBilling(tenantSlug, noteId, patch);
  } catch (err) {
    throw new CombinedSaveError('billing', summary !== undefined, err);
  }
}

/** Which half of the combined Save failed. The line rides first, so
 *  `lineSaved` is only true when the billing half failed AFTER the line
 *  landed (the one partial-success shape). `cause` is the half's own
 *  failure, so a refusal that carries the server's sentence (the note
 *  author rule's 403, the invoice freeze) can show it. */
export class CombinedSaveError extends Error {
  constructor(
    readonly failed: 'line' | 'billing',
    readonly lineSaved: boolean,
    override readonly cause: unknown,
  ) {
    super(`combined save failed at the ${failed} half`);
  }
}

/** The one field refusal this editor can meet: the admin band with after
 *  hours, which the server refuses on field after_hours. The sentence is
 *  this screen's own; the server's message is never read. */
function afterHoursRefusal(e: unknown): string | null {
  if (!(e instanceof ApiError) || errorCodeOf(e) !== 'invalid_body') return null;
  const field = (e.body as { field?: unknown } | null)?.field;
  return field === 'after_hours'
    ? "The admin rate band can't be After Hours, so nothing was saved. Choose Regular, or pick another band."
    : null;
}

/** The one partial-success shape, named honestly: the line landed and
 *  the billing half did not. */
const LINE_SAVED_BILLING_DID_NOT = "The invoice line saved, but the hours and rate didn't. Save again to finish.";

/** Plain words for each combined-save failure: the server's own
 *  sentence when the refusal's code carries one, the after-hours refusal,
 *  a refusal a retry cannot fix, else which half failed and where that
 *  leaves the row. */
function combinedSaveMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof CombinedSaveError) {
    const server =
      serverMessageFrom(err.cause) ?? afterHoursRefusal(err.cause) ?? refusalText(err.cause, contact);
    if (server !== null) return server;
    if (err.failed === 'line') {
      return contactSentence(
        "The invoice line didn't save, so nothing changed. Try again, and if it happens again, tell {contact_system}.",
        contact,
      );
    }
    return err.lineSaved
      ? LINE_SAVED_BILLING_DID_NOT
      : contactSentence(
          "The hours and rate didn't save. Check them and save again, and if it still fails, tell {contact_system}.",
          contact,
        );
  }
  return contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact);
}

/** An editor row's failure, in its two places: the server's refusal of
 *  the date sits under the date box (`date`); everything else is the
 *  problem line under the buttons (`message`). */
export interface EditProblem {
  message: string | null;
  date: string | null;
}

/** The combined Save's failure, split for the row. A refused date goes
 *  under the date box and leaves the problem line empty, unless the line
 *  half saved first, which the problem line then says. */
export function editProblemFor(err: unknown, contact: SupportContact): EditProblem {
  const cause = err instanceof CombinedSaveError ? err.cause : err;
  const date = noteDateRefusal(cause);
  if (date === null) return { message: combinedSaveMessage(err, contact), date: null };
  const lineSaved = err instanceof CombinedSaveError && err.lineSaved;
  return { message: lineSaved ? LINE_SAVED_BILLING_DID_NOT : null, date };
}

/** The summary cell: the line the family sees (the server's rendered
 *  invoice_line; a 0-hour line opens with "No charge.") when there is
 *  one, the standing language when there is not. The editor edits the
 *  stored summary, never this. */
export function SummaryCell({ note }: { note: PrebillingNote }): ReactNode {
  const { tenantSlug } = useTenant();
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  if (note.readiness === 'needs_detail') {
    // The standing no-line copy (kickbackCopy.ts, wording is law): the
    // hours framing only for a note kicked back above zero hours.
    return (
      <span className="text-caution-deep" data-testid={`prebilling-kickback-${note.noteId}`}>
        {insufficientContentMessage(note.hours, note.kickedBack)}
      </span>
    );
  }
  if (note.readiness === 'still_reading') {
    return <span className="italic text-ink-soft">Still reading this note.</span>;
  }
  if (note.readiness === 'failed') {
    return (
      <span className="text-pepper">
        This note didn't go through, so it isn't on the invoice yet. Retry it from Notes &amp; activity.
      </span>
    );
  }
  if (note.invoiceLine !== null) {
    // A line that says no charge but carries hours is billed: say so. A
    // warning, never a refusal: the switch and Edit are untouched.
    const warning = noChargeHoursWarningFor(
      note.hoursEntered ? note.hours : null,
      note.rateBand,
      note.summary,
      readRateBands(tenantConfig?.config),
    );
    // A line typed in capitals is never re-cased (names cannot be told from
    // words): ask the person who wrote it to retype it.
    const inCapitals = isInCapitals(note.summary);
    if (warning === null && !inCapitals) return <span className="text-ink">{note.invoiceLine}</span>;
    return (
      <>
        <span className="text-ink">{note.invoiceLine}</span>
        {warning !== null && (
          <p data-testid={`no-charge-hours-warning-${note.noteId}`} className="text-xs text-caution-deep">
            {warning}
          </p>
        )}
        {inCapitals && (
          <p data-testid={`caps-line-warning-${note.noteId}`} className="text-xs text-caution-deep">
            {CAPITALS_WARNING}
          </p>
        )}
      </>
    );
  }
  return null;
}


// ---------------------------------------------------------------------
// One pre-billing row — read view + the ONE combined inline editor
// (billing fields + the invoice line) + the human Ready switch.
// ---------------------------------------------------------------------

/** The Status column's width: the widest line it holds (the switch with
 *  "Marked by" or the Waiting chip after it), so nothing in it wraps when
 *  a row is marked. Edit has its own column (EDIT_COLUMN_WIDTH). */
export const STATUS_COLUMN_WIDTH = 'w-[23.5rem]';

/** My time's Status column: every row is the viewer's own note, so it
 *  never holds the Waiting chip, only the switch and "Marked by". */
export const OWN_NOTES_STATUS_COLUMN_WIDTH = 'w-[21rem]';

/** The Edit column's width: the ghost Edit button and its gutter. */
export const EDIT_COLUMN_WIDTH = 'w-[4rem]';

/** A table's header words for a column: the Edit column's header is for
 *  a screen reader only, since every button under it already says Edit. */
export function columnHeading(column: string): ReactNode {
  return column === 'Edit' ? <span className="sr-only">Edit</span> : column;
}

/** The Ready switch's two halves: the shared SegmentedToggle, Not ready in
 *  the faded terracotta while it waits, Ready in the primary fill. */
const READY_OPTIONS: readonly [SegmentedOption<boolean>, SegmentedOption<boolean>] = [
  { value: false, label: 'Not ready', suffix: 'not-ready', checkedLook: 'terracotta' },
  { value: true, label: 'Ready', suffix: 'ready' },
];

export interface PrebillingRowProps {
  note: PrebillingNote;
  bands: readonly { value: string; label: string }[];
  /** 0134 — the actor-scoped subset of `bands` the editor may OFFER. */
  allowedBands: readonly { value: string; label: string }[];
  bandLabels: Record<string, string>;
  /** The viewer holds view_invoice: the row shows its band column, its
   *  rate and the export-only marker. */
  officeSeat: boolean;
  /** How many columns the header draws: an open edit box spans them. */
  columnCount: number;
  /** The band's rate in effect today (wire string), or null — rendered
   *  as quiet money or the "No rate on file" shadow (never guessed). */
  currentRate: string | null;
  /** The tenant's native_from boundary — feeds the export-only marker's
   *  explanation text (proseDate'd date, or the unset wording). */
  nativeFrom: string | null;
  /** The mapped mark-ready refusal for THIS row (rate_not_on_file), or null. */
  readyProblem: string | null;
  /** The last save's sentence for THIS row when an office edit left the
   *  note billed by export, or null. */
  editNotice?: string | null;
  /** The combined Save's failure for THIS row (which half; a refused date
   *  apart), or null. */
  editProblem: EditProblem | null;
  /** The viewer wrote this note: the awaiting chip reads "Needs your OK"
   *  to them and "Waiting for the care manager" to anyone else. */
  viewerIsAuthor: boolean;
  /** The viewer's user id (null while unknown): the edit box says whose
   *  hours these are when it is not the author's. */
  viewerUserId: string | null;
  /** The viewer may edit this row. Gates Edit. */
  canEditLine: boolean;
  /** The viewer may set this row Ready or Not ready. Gates the switch.
   *  The office follows the note author rule; a seat without
   *  view_invoice, its own notes only. */
  canMark: boolean;
  isEditing: boolean;
  /** This row's box just closed by Cancel or a successful save: its Edit
   *  button takes focus back, then `onFocusReturned` clears the request. */
  returnFocus: boolean;
  onFocusReturned: () => void;
  onStartEdit: () => void;
  onCancel: () => void;
  /** The date box changed after a refused date: its sentence goes. */
  onDateChanged: () => void;
  onSave: (patch: BillingPatch, summary?: string) => void;
  /** The line-only save, for a seat without view_invoice. */
  onSaveLine: (summary: string) => void;
  /** The value this row's switch was just set to while its request is in
   *  flight, else undefined. */
  readyPending: boolean | undefined;
  onSetReady: (ready: boolean) => void;
  /** An edit save is in flight on this row. */
  saving: boolean;
  /** The viewer may remove this note (the Notes tab's rule,
   *  canChangeNote): the edit box shows Remove note. */
  canRemove: boolean;
  /** Remove the note, after the confirm. */
  onRemove: () => void;
  /** A remove is in flight on this row. */
  removing: boolean;
  /** The remove's refusal for THIS row, in plain words, or null. */
  removeProblem: string | null;
  /** My time's list: the client the note is for, shown beside the date,
   *  and the row's hours in My time's "1.5 h". Absent on the client
   *  record's own Pre-billing. */
  client?: PrebillingRowClient;
  /** A table row (the default) or, on My time below `sm`, a card holding
   *  the same fields. */
  layout?: 'table' | 'card';
}

/** The client a My time row is for. */
export interface PrebillingRowClient {
  name: string;
  /** The note on the client's record, or null when the viewer's scope no
   *  longer reaches the client (the name is then plain text). */
  href: string | null;
  inScope: boolean;
  demo: boolean;
  /** The note's rate band, in words. */
  bandLabel: string;
}

/** A row's hours in My time's words: one decimal and "h" ("1.5 h", "0 h"). */
function myTimeHours(note: PrebillingNote): string {
  return note.hoursEntered ? `${formatHoursTenths(note.hours.toFixed(2))} h` : 'No hours';
}

/** A refused mark, said in plain words. In a card it sits under the
 *  switch; in a table it is a line of its own under the row
 *  (ReadyProblemRow), so the row keeps its one height. */
function ReadyProblem({ noteId, message }: { noteId: string; message: string }): ReactNode {
  return (
    <p role="alert" className="text-[11px] text-pepper whitespace-normal" data-testid={`prebilling-ready-problem-${noteId}`}>
      {message}
    </p>
  );
}

/** A table's line under a row whose mark was refused: the refusal, as
 *  wide as the table, so the row above it keeps its one height. */
export function ReadyProblemRow({
  noteId,
  message,
  columnCount,
}: {
  noteId: string;
  message: string | null;
  columnCount: number;
}): ReactNode {
  if (message === null) return null;
  return (
    <tr data-testid={`prebilling-ready-problem-row-${noteId}`}>
      <td colSpan={columnCount} className="pb-2">
        <ReadyProblem noteId={noteId} message={message} />
      </td>
    </tr>
  );
}

/**
 * A Pre-billing line's state, on one line: the Ready switch on a line the
 * viewer may mark, else the state chip; then, after it on the same line,
 * its state words ("Marked by" on a Ready line, the Waiting chip on
 * another author's awaiting line under a switch, else nothing). The
 * client's Pre-billing tab and the all-client Pre-billing page both draw
 * it, so the two read the same. `children` sit at the end of the line (a
 * My time card's Edit). A refusal shows under the line in a card
 * (`problemBelow`); a table draws it as ReadyProblemRow instead. Where a
 * table has no room for the words beside the switch (the all-client page,
 * `stateWords` 'tooltip'), they are the switch line's tooltip and are
 * still read aloud. A card (`stateWords` 'below') is narrow, so the words
 * take a line of their own under the switch whose height is kept, empty
 * or not: marking a card fills that line and moves nothing.
 */
export function PrebillingReadyCell({
  note,
  canMark,
  viewerIsAuthor,
  readyPending,
  onSetReady,
  readyProblem,
  problemBelow = true,
  stateWords = 'beside',
  switchName,
  children,
}: {
  note: Pick<PrebillingNote, 'noteId' | 'readiness' | 'occurredAt' | 'billingReadyBy' | 'billingReadyByViewer'>;
  canMark: boolean;
  viewerIsAuthor: boolean;
  readyPending: boolean | undefined;
  onSetReady: (ready: boolean) => void;
  readyProblem: string | null;
  /** The refusal shows under the line (a card). A table passes false and
   *  draws ReadyProblemRow under the row. */
  problemBelow?: boolean;
  /** Beside the switch (the default); where the table has no room for
   *  them, the switch line's tooltip, still read aloud; in a card, a kept
   *  line under the switch. */
  stateWords?: 'beside' | 'tooltip' | 'below';
  /** The switch's accessible name; the tab's default names the date. A
   *  page listing many clients names the client too. */
  switchName?: string;
  children?: ReactNode;
}): ReactNode {
  const chip = chipFor(note, viewerIsAuthor);
  const showSwitch = canMark && (note.readiness === 'needs_your_ok' || note.readiness === 'ready');
  const waitingBesideSwitch = showSwitch && note.readiness === 'needs_your_ok' && !viewerIsAuthor;
  const isReady = readyPending ?? note.readiness === 'ready';
  function setReady(next: boolean): void {
    // Nothing more while this line's request is in flight, and nothing for
    // the value already shown.
    if (readyPending !== undefined || next === isReady) return;
    onSetReady(next);
  }
  // The server says whether the person reading marked it (no name or id
  // is compared here), else the marker's name.
  const markedBy = note.billingReadyByViewer
    ? 'Marked by you'
    : note.billingReadyBy
      ? `Marked by ${note.billingReadyBy}`
      : 'Marked ready';
  // The words a switch line says after the switch, for its tooltip.
  const wordsBesideSwitch = !showSwitch
    ? undefined
    : note.readiness === 'ready'
      ? markedBy
      : waitingBesideSwitch
        ? chip.label
        : undefined;
  const asTooltip = stateWords === 'tooltip' && showSwitch;
  const below = stateWords === 'below';
  const markedByWords = (
    <span className="whitespace-nowrap text-[0.6875rem] text-ink-soft" data-testid={`prebilling-ready-by-${note.noteId}`}>
      {markedBy}
    </span>
  );
  return (
    <div className="flex flex-col items-start gap-1.5">
      {/* One line: the switch or chip, then the state words. Marking a row
          fills the words in place; nothing before them moves. */}
      <div
        className="flex flex-wrap items-center gap-x-2 gap-y-1"
        title={asTooltip ? wordsBesideSwitch : undefined}
        data-testid={`prebilling-status-line-${note.noteId}`}
      >
        {!showSwitch && (
          <Chip variant={chip.variant} data-testid={`prebilling-state-${note.noteId}`}>
            {chip.label}
          </Chip>
        )}
        {showSwitch && (
          // A plain wrapper: the switch keeps its own width in the cell.
          <div className="min-w-max">
            <SegmentedToggle
              options={READY_OPTIONS}
              value={isReady}
              onChange={setReady}
              name={switchName ?? `Ready to bill, ${proseDate(note.occurredAt)}`}
              testIdPrefix={`prebilling-ready-toggle-${note.noteId}`}
            />
          </div>
        )}
        {/* The state words after the switch: always there, on the line. */}
        {showSwitch && !below && (
          <span
            className={asTooltip ? 'sr-only' : 'inline-flex items-center'}
            data-testid={`prebilling-state-line-${note.noteId}`}
          >
            {note.readiness === 'ready'
              ? markedByWords
              : waitingBesideSwitch && (
                  <Chip variant={chip.variant} data-testid={`prebilling-state-${note.noteId}`}>
                    {chip.label}
                  </Chip>
                )}
          </span>
        )}
        {/* A Ready line the viewer may not mark: who confirmed it. */}
        {!showSwitch && note.readiness === 'ready' && markedByWords}
        {children}
      </div>

      {/* A card's state words: their own line under the switch, one height
          whatever it holds. */}
      {showSwitch && below && (
        <div className="flex h-6 items-center" data-testid={`prebilling-state-line-${note.noteId}`}>
          {note.readiness === 'ready'
            ? markedByWords
            : waitingBesideSwitch && (
                <Chip variant={chip.variant} data-testid={`prebilling-state-${note.noteId}`}>
                  {chip.label}
                </Chip>
              )}
        </div>
      )}

      {/* The mapped refusal (0135 rate gate), named, with the fix in the
          same sentence. */}
      {problemBelow && readyProblem !== null && <ReadyProblem noteId={note.noteId} message={readyProblem} />}
    </div>
  );
}

/** The client cell on My time: the name (a link to the note while the
 *  viewer's scope reaches the client), the band under it with the After
 *  Hours mark beside it, the Demo chip, and the line that says the client
 *  has left her care team. */
function ClientCell({ note, client }: { note: PrebillingNote; client: PrebillingRowClient }): ReactNode {
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium leading-5 break-words">
        {client.href !== null ? (
          <Link to={client.href} className="text-sage-800 hover:underline">
            {client.name}
          </Link>
        ) : (
          <span className="text-sage-800">{client.name}</span>
        )}
      </p>
      <p className="flex items-center gap-1.5 text-xs text-ink-soft">
        <span className="min-w-0">{client.bandLabel}</span>
        <AfterHoursMark note={note} />
      </p>
      {client.demo && (
        <p className="mt-1">
          <Chip variant="neutral">Demo client</Chip>
        </p>
      )}
      {!client.inScope && <p className="mt-0.5 text-xs italic text-ink-soft">Not on your care team now</p>}
    </div>
  );
}

/** Editing a Ready line takes it off Ready: the line at the top of the
 *  box says who marks it again. A seat without view_invoice reads its own
 *  sentence; the office reads it on another author's line only. */
export function readyHintFor(
  note: Pick<PrebillingNote, 'readiness'>,
  officeSeat: boolean,
  viewerIsAuthor: boolean,
): string | null {
  if (note.readiness !== 'ready') return null;
  if (!officeSeat) return 'Mark it Ready again after editing to finalize billing.';
  return viewerIsAuthor ? null : 'The care manager marks it Ready again.';
}

export function PrebillingRow({
  note,
  bands,
  allowedBands,
  bandLabels,
  officeSeat,
  columnCount,
  currentRate,
  nativeFrom,
  readyProblem,
  editNotice = null,
  editProblem,
  viewerIsAuthor,
  viewerUserId,
  canEditLine,
  canMark,
  isEditing,
  returnFocus,
  onFocusReturned,
  onStartEdit,
  onCancel,
  onDateChanged,
  onSave,
  onSaveLine,
  readyPending,
  onSetReady,
  saving,
  canRemove,
  onRemove,
  removing,
  removeProblem,
  client,
  layout = 'table',
}: PrebillingRowProps): ReactNode {

  // The Edit button mounts again when the box closes; a close by Cancel
  // or a successful save hands focus back to it.
  const editRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isEditing && returnFocus) {
      editRef.current?.focus();
      onFocusReturned();
    }
  }, [isEditing, returnFocus, onFocusReturned]);

  const readyHint = readyHintFor(note, officeSeat, viewerIsAuthor);


  // The one Edit, hidden only while this row's box is open. A table draws
  // it in its own column; a card, at the end of the status line.
  const editButton = canEditLine &&
    note.readiness !== 'still_reading' &&
    note.readiness !== 'failed' &&
    !isEditing && (
      <Button
        ref={editRef}
        variant="ghost"
        size="sm"
        onClick={onStartEdit}
        data-testid={`prebilling-edit-${note.noteId}`}
      >
        Edit
      </Button>
    );

  const statusLine = (
    <PrebillingReadyCell
      note={note}
      canMark={canMark}
      viewerIsAuthor={viewerIsAuthor}
      readyPending={readyPending}
      onSetReady={onSetReady}
      readyProblem={readyProblem}
      problemBelow={layout === 'card'}
      stateWords={layout === 'card' ? 'below' : 'beside'}
    >
      {layout === 'card' && editButton}
    </PrebillingReadyCell>
  );

  // The export-only marker: this Ready line downloads in the CSV but will
  // not appear on an invoice, either because the workspace's native
  // billing is OFF (nativeFrom null; the tab's banner carries that alarm
  // once) or because the note predates the start. AMBIENT, in the Rate
  // cell's quiet line beside the price: neutral words, never gold or
  // pepper, on a Ready line only (it names a fact about a line that
  // already exports; it never blocks anything).
  const exportOnly = officeSeat &&
    note.readiness === 'ready' &&
    (nativeFrom === null || note.beforeNativeStart) && (
      <span
        className="text-ink-soft"
        title={nativeExportOnlyExplanation(nativeFrom)}
        data-testid={`prebilling-export-only-${note.noteId}`}
      >
        Export only
      </span>
    );
  // The band's rate today, or the missing-rate shadow: visible before a
  // row is set Ready, so the fail-closed rate gate never reads as a
  // surprise. A zero-hour note needs no rate, so it shows neither. Only
  // the office seat draws the Rate cell: no other seat reads a rate.
  const price =
    note.rateBand !== null &&
    !(note.hoursEntered && note.hours === 0) &&
    (currentRate !== null ? (
      <span className="text-ink-soft" data-testid={`prebilling-rate-${note.noteId}`}>
        {formatMoney(currentRate)} / hour
      </span>
    ) : (
      <span className="italic text-pepper" data-testid={`prebilling-norate-${note.noteId}`}>
        No rate on file
      </span>
    ));

  const editor = isEditing && (
    <EditRow
      note={note}
      bands={bands}
      allowedBands={allowedBands}
      lineOnly={!officeSeat && !viewerIsAuthor}
      columnCount={columnCount}
      readyHint={readyHint}
      notAuthor={editorIsNotAuthor(viewerUserId, note.authorUserId)}
      problem={editProblem}
      onDateChanged={onDateChanged}
      onSave={onSave}
      onSaveLine={onSaveLine}
      onCancel={onCancel}
      saving={saving}
      canRemove={canRemove}
      onRemove={onRemove}
      removing={removing}
      removeProblem={removeProblem}
      layout={layout}
    />
  );

  // My time below `sm`: one card per note, the same fields and the same
  // status, the edit box inside the card.
  if (layout === 'card' && client !== undefined) {
    return (
      <li
        data-testid={`my-prebilling-card-${note.noteId}`}
        className="flex flex-col gap-2 rounded-base border border-sage-800/[10%] bg-paper p-3"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-ink-soft">{proseDate(note.occurredAt)}</span>
          <span className="whitespace-nowrap text-sm tabular-nums text-ink">{myTimeHours(note)}</span>
        </div>
        <ClientCell note={note} client={client} />
        <div className="text-[0.78125rem] leading-relaxed">
          <SummaryCell note={note} />
        </div>
        {statusLine}
        {editNotice !== null && !isEditing && (
          <p role="status" className="text-xs text-ink" data-testid="prebilling-edit-left-invoice">
            {editNotice}
          </p>
        )}
        {editor}
      </li>
    );
  }

  // The read row stays while its box is open, and the box row follows it.
  // While open, the row's Edit button is the one thing hidden: the divider
  // under the row goes too, so the box reads as the row's own. Every cell
  // but Summary is one line (Rate's quiet second line keeps its height
  // empty), so every row is one height; a refusal or a save's notice is a
  // line of its own under the row.
  return (
    <>
    <tr data-testid={`prebilling-row-${note.noteId}`} className={isEditing ? 'border-b-0' : undefined}>
      <td className="py-2 pr-3 whitespace-nowrap align-top text-sage-800">
        {rowDate(note.occurredAt)}
      </td>
      {client !== undefined && (
        <td className="py-2 pr-3 align-top">
          <ClientCell note={note} client={client} />
        </td>
      )}
      <td className="py-2 pr-3 whitespace-nowrap align-top text-sage-800">
        {/* A seat without view_invoice has no Rate column, so their After
            Hours mark sits beside the hours (on My time, in the client
            cell). */}
        <span className="inline-flex items-center gap-1.5">
          {client !== undefined ? (
            myTimeHours(note)
          ) : note.hoursEntered ? (
            formatHours(note.hours)
          ) : (
            <span className="italic text-ink-soft">No hours</span>
          )}
          {!officeSeat && client === undefined && <AfterHoursMark note={note} />}
        </span>
      </td>
      {officeSeat && (
        <td className="py-2 pr-3 align-top text-sage-800" data-testid={`prebilling-rate-cell-${note.noteId}`}>
          <RateCell
            note={note}
            bandLabels={bandLabels}
            quietLine={
              <>
                {price}
                {price && exportOnly && <span aria-hidden="true"> · </span>}
                {exportOnly}
              </>
            }
          />
        </td>
      )}
      <td className="py-2 pr-3 align-top text-[12.5px] leading-relaxed">
        <SummaryCell note={note} />
      </td>
      {/* Status: one line, the chip or the Ready switch with its state
          words after it. Marking a row changes the switch in place and
          fills the words: nothing on the row moves, the row does not grow,
          and focus stays on the switch. */}
      <td className="py-2 pr-3 align-top">
        {statusLine}
      </td>
      <td className="py-2 align-top" data-testid={`prebilling-edit-cell-${note.noteId}`}>
        {editButton}
      </td>
    </tr>
    <ReadyProblemRow noteId={note.noteId} message={readyProblem} columnCount={columnCount} />
    {editNotice !== null && !isEditing && (
      <tr>
        <td colSpan={columnCount} className="pb-2">
          <p role="status" className="text-xs text-ink" data-testid="prebilling-edit-left-invoice">
            {editNotice}
          </p>
        </td>
      </tr>
    )}
    {editor}
    </>
  );
}

/** The After Hours mark, on an after-hours note: a small mark in the
 *  After Hours gold of the Regular | After Hours switch, beside the band
 *  on the band's own line (LP-18), or, for a seat without view_invoice,
 *  beside the hours. It never takes a line of its own. */
export function AfterHoursMark({ note }: { note: Pick<PrebillingNote, 'noteId' | 'afterHours'> }): ReactNode {
  if (!note.afterHours) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-tight bg-gold-soft px-1.5 text-[0.6875rem] font-semibold leading-[1.125rem] text-caution-deep"
      data-testid={`prebilling-after-hours-${note.noteId}`}
    >
      After Hours
    </span>
  );
}

/** The band's label with, on an after-hours note, the After Hours mark
 *  beside it: one line, inside its cell. */
export function BandText({
  note,
  bandLabels,
}: {
  note: Pick<PrebillingNote, 'noteId' | 'afterHours' | 'rateBand'>;
  bandLabels: Record<string, string>;
}): ReactNode {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap"
      data-testid={`prebilling-band-line-${note.noteId}`}
    >
      <span data-testid={`prebilling-band-${note.noteId}`}>
        {rateBandLabel(note.rateBand, bandLabels) || <span className="italic text-ink-soft">None</span>}
      </span>
      <AfterHoursMark note={note} />
    </span>
  );
}

/** The Rate cell: the band (and the After Hours mark) on the first line;
 *  with `quietLine`, a quiet second line (the price per hour, Export
 *  only) that keeps its height when it holds nothing, so every row is one
 *  height. */
export function RateCell({
  note,
  bandLabels,
  quietLine,
}: {
  note: Pick<PrebillingNote, 'noteId' | 'afterHours' | 'rateBand'>;
  bandLabels: Record<string, string>;
  quietLine?: ReactNode;
}): ReactNode {
  return (
    <>
      <div className="flex">
        <BandText note={note} bandLabels={bandLabels} />
      </div>
      {quietLine !== undefined && (
        <div
          className="h-4 whitespace-nowrap text-[11px] leading-4"
          data-testid={`prebilling-rate-quiet-${note.noteId}`}
        >
          {quietLine}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// The ONE edit box: it opens under the line it edits, in a row of one
// cell spanning the table, as wide as the table's visible area and stuck
// to its left edge while the table scrolls sideways. The office's box
// holds the date, hours, rate band, time of day and invoice line with one
// Save; a seat without view_invoice (`lineOnly`) edits the invoice line
// alone, and an unchanged line closes the box and sends nothing. Each
// field's problem shows under that field, and a save's failure above the
// buttons. Saving takes the row off Ready on the server.
// ---------------------------------------------------------------------

/** The date box was emptied: the line needs a date. */
const CHOOSE_DATE = 'Choose the date for this line.';

/** The field sentences a press of Save found, one per box; null is no
 *  gap. Each goes when its own box next changes. */
interface Gaps {
  date: string | null;
  hours: string | null;
  line: string | null;
}
const NO_GAPS: Gaps = { date: null, hours: null, line: null };

/** The hours sentence for a value that is not a usable number, or off the
 *  practice's step; null when the hours are fine. */
function hoursProblem(hours: string, hoursEntered: boolean, hoursIncrement: string): string | null {
  const n = Number(hours);
  if (hours.trim() === '' || Number.isNaN(n) || n < 0) {
    return hoursEntered
      ? 'Enter hours as a number: 1.5 is an hour and a half. Use 0 for a no-charge visit.'
      : 'Enter the hours as a number: 1.5 is an hour and a half.';
  }
  // An off-step value is refused in plain words: a billing quantity is
  // never silently rounded.
  if (!isOnHoursIncrement(n, hoursIncrement)) return hoursIncrementMessage(hoursIncrement, n);
  return null;
}

/** The ids an input's aria-describedby lists: its help and, while shown,
 *  its sentence. */
function describedBy(id: string, help: boolean, error: boolean): string | undefined {
  const ids = [help ? `${id}-help` : null, error ? `${id}-error` : null].filter((v) => v !== null);
  return ids.length === 0 ? undefined : ids.join(' ');
}

/** The line checks both editors share, in the office editor's words. */
function lineProblem(line: string, originalLine: string): string | null {
  if (line === '' && originalLine !== '') {
    return 'Add a line for the invoice, or leave the note as is.';
  }
  // A line that is only "No charge" is no line: the summary PATCH
  // refuses it, so nothing is sent.
  if (line !== '' && !hasInvoiceLine(line)) return MARKER_ONLY_HINT;
  return null;
}

export function EditRow({
  note,
  bands,
  allowedBands,
  lineOnly,
  columnCount,
  readyHint,
  notAuthor,
  problem,
  onDateChanged,
  onSave,
  onSaveLine,
  onCancel,
  saving,
  canRemove,
  onRemove,
  removing,
  removeProblem,
  layout = 'table',
}: {
  note: PrebillingNote;
  bands: readonly { value: string; label: string }[];
  /** 0134 — the actor-scoped bands this editor may OFFER as a change. */
  allowedBands: readonly { value: string; label: string }[];
  /** The invoice line is the only field (a seat without view_invoice). */
  lineOnly: boolean;
  /** How many columns the table has: the box's one cell spans them. */
  columnCount: number;
  /** The quiet line at the top of the box saying who marks the row Ready
   *  again, or null when the row is not Ready or the viewer wrote it. */
  readyHint: string | null;
  /** The viewer did not write this note: the box says the hours stay the
   *  author's. */
  notAuthor: boolean;
  /** The combined Save's named failure (which half; a refused date apart),
   *  or null. */
  problem: EditProblem | null;
  /** The date box changed after a refused date. */
  onDateChanged: () => void;
  /** `summary` rides only when the line actually changed (the summary
   *  PATCH refuses empty text — blanking a line is not a thing). */
  onSave: (patch: BillingPatch, summary?: string) => void;
  /** The line-only save: sent only when the line changed. */
  onSaveLine: (summary: string) => void;
  onCancel: () => void;
  saving: boolean;
  /** Remove note shows (the Notes tab's rule). */
  canRemove: boolean;
  /** Remove the note; called once the confirm is accepted. */
  onRemove: () => void;
  removing: boolean;
  /** The remove's refusal, in plain words, or null. */
  removeProblem: string | null;
  /** A table row (the default), or the box alone inside a My time card. */
  layout?: 'table' | 'card';
}): ReactNode {
  // Remove note asks first; nothing is sent until its confirm.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // LP-45: the note's pay period has closed and this seat is not the
  // office (payInputsLocked, the server's word). The date, hours, rate and
  // time of day show as they are and can't change; the line still saves,
  // on its own (no billing half is sent, so nothing else moves). Remove
  // follows the period itself (payPeriodClosed), the office included, as
  // My time's locked entries are not offered for removal.
  const payLocked = !lineOnly && note.payInputsLocked;
  // Hours never entered seed a blank field, so a save states them rather
  // than writing a zero no one typed.
  const [hours, setHours] = useState(
    note.hoursEntered ? String(Number(note.hours.toFixed(2))) : '',
  );
  const [rateBand, setRateBand] = useState(note.rateBand ?? '');
  const [afterHours, setAfterHours] = useState(note.afterHours);
  const [occurredAt, setOccurredAt] = useState(note.occurredAt);
  const [summary, setSummary] = useState(note.summary ?? '');
  // The sentences the last press of Save found, one under each box.
  const [gaps, setGaps] = useState<Gaps>(NO_GAPS);
  const dateRef = useRef<HTMLInputElement>(null);
  const hoursRef = useRef<HTMLInputElement>(null);
  const lineRef = useRef<HTMLTextAreaElement>(null);
  // The ids: each box's label, help and sentence hang off its own.
  const dateId = `prebilling-${note.noteId}-date`;
  const hoursId = `prebilling-${note.noteId}-hours`;
  const bandId = `prebilling-${note.noteId}-band`;
  const timeOfDayId = `prebilling-${note.noteId}-timeofday`;
  const lineId = `prebilling-${note.noteId}-line`;
  // A refused date: its sentence sits under the date box, which keeps
  // what was typed and takes focus. The empty-date sentence comes first,
  // since a refusal can only follow a send.
  const dateProblem = problem?.date ?? null;
  const problemLine = problem?.message ?? null;
  const dateError = gaps.date ?? dateProblem ?? undefined;
  useEffect(() => {
    if (dateProblem !== null) dateRef.current?.focus();
  }, [dateProblem]);
  // Opening the box moves focus to its first field.
  useEffect(() => {
    (lineOnly || payLocked ? lineRef : dateRef).current?.focus();
  }, [lineOnly, payLocked]);
  // Billable time moves in tenant-config steps (billing.hours_increment,
  // '0.1' fallback). Read here directly: the shared ['tenant-config']
  // cache entry, no prop threading through the row chain. The server
  // enforces the same rule on the PATCH.
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const hoursIncrement = readHoursIncrement(tenantConfig?.config);
  // No after-hours admin, and no after-hours per-visit service.
  const tenantBands = readRateBands(tenantConfig?.config);
  const noAfterHours = rateBand === ADMIN_RATE_BAND || isPerVisitBand(rateBand, tenantBands);

  // 0134: the select offers the actor's allowed bands, PLUS the note's
  // current band when it sits outside that set (an office editor fixing a
  // CM note's hours must see — and keep — the CM's band honestly, even
  // though they could not pick it themselves).
  const currentBand = note.rateBand ?? '';
  const bandChoices =
    currentBand === '' || allowedBands.some((b) => b.value === currentBand)
      ? allowedBands
      : [
          {
            value: currentBand,
            label: bands.find((b) => b.value === currentBand)?.label ?? currentBand,
          },
          ...allowedBands,
        ];

  // The line is required only when the note already had one: a blanked
  // line is refused then, and never sent.
  const originalLine = note.summary ?? '';
  const lineRequired = originalLine !== '';
  function save(): void {
    const line = summary.trim();
    if (lineOnly || payLocked) {
      const lineGap = lineProblem(line, originalLine);
      if (lineGap !== null) {
        setGaps({ ...NO_GAPS, line: lineGap });
        lineRef.current?.focus();
        return;
      }
      setGaps(NO_GAPS);
      if (line === originalLine) {
        onCancel();
        return;
      }
      onSaveLine(line);
      return;
    }
    // Every box is checked, each gap shows under its own box, and focus
    // moves to the first: date, then hours, then the line. Nothing is
    // sent while there is a gap.
    const found: Gaps = {
      date: occurredAt === '' ? CHOOSE_DATE : null,
      hours: hoursProblem(hours, note.hoursEntered, hoursIncrement),
      line: lineProblem(line, originalLine),
    };
    if (found.date !== null || found.hours !== null || found.line !== null) {
      setGaps(found);
      (found.date !== null ? dateRef : found.hours !== null ? hoursRef : lineRef).current?.focus();
      return;
    }
    setGaps(NO_GAPS);
    // hours rides as a NUMBER (the server's coerceNoteHours rejects
    // strings — the old editor's String(n) 400'd on every hours edit).
    // No after-hours admin: an admin band never rides the flag, whatever
    // the toggle read before the band changed.
    const patch: BillingPatch = {
      hours: Number(hours),
      after_hours: noAfterHours ? false : afterHours,
      occurred_at: occurredAt,
    };
    // 0134: only ride rate_band on the wire when it actually changed — an
    // hours-only edit on a note whose band the ACTOR could not pick must
    // not trip the server's allowed-band guard.
    if (rateBand !== currentBand) {
      patch.rate_band = rateBand === '' ? null : rateBand;
    }
    // The line rides only when it changed (and never as empty — the
    // summary endpoint refuses blanking by design).
    onSave(patch, line !== '' && line !== originalLine ? line : undefined);
  }

  const input = 'rounded-base border border-sage-300 px-2 py-1 text-sm';

  const box = (
        <div
          data-testid={`prebilling-edit-box-${note.noteId}`}
          className={`${
            layout === 'card' ? 'w-full' : 'sticky left-0 box-border w-[100cqw]'
          } rounded-md border border-sage-800/[14%] bg-paper p-3 flex flex-col gap-3`}
        >
          {readyHint !== null && (
            <p className="text-xs text-ink-soft whitespace-normal" data-testid={`prebilling-edit-hint-${note.noteId}`}>
              {readyHint}
            </p>
          )}
          {notAuthor && !payLocked && (
            <p
              className="text-xs text-ink whitespace-normal"
              data-testid={`prebilling-edit-author-hours-${note.noteId}`}
            >
              {NOT_THE_AUTHOR_EDIT_LINE}
            </p>
          )}
          {payLocked && (
            <p
              id={`prebilling-${note.noteId}-pay-locked`}
              className="text-xs text-ink whitespace-normal"
              data-testid={`prebilling-edit-pay-locked-${note.noteId}`}
            >
              {PAY_PERIOD_CLOSED_EDIT_LINE}
            </p>
          )}
          {/* As many 15rem columns as fit: one on a phone, two at 200% text
              on a 1280 screen, four on a wide screen. The line spans them. */}
          <div className="grid gap-x-4 gap-y-3 grid-cols-[repeat(auto-fill,minmax(min(100%,15rem),1fr))]">
            {!lineOnly && (
              <>
                <Field label="Date" required={!payLocked} htmlFor={dateId} error={dateError}>
                  <DateInput
                    size="compact"
                    id={dateId}
                    ref={dateRef}
                    value={occurredAt}
                    max={practiceTodayIso()}
                    disabled={payLocked}
                    aria-required="true"
                    aria-invalid={dateError !== undefined ? true : undefined}
                    aria-describedby={describedBy(dateId, false, dateError !== undefined)}
                    onChange={(e) => {
                      setOccurredAt(e.target.value);
                      if (dateProblem !== null) onDateChanged();
                      setGaps((g) => (g.date === null ? g : { ...g, date: null }));
                    }}
                    className="w-full max-w-[11rem]"
                    data-testid={`prebilling-input-date-${note.noteId}`}
                  />
                </Field>
                <Field
                  label="Hours"
                  required={!payLocked}
                  htmlFor={hoursId}
                  help={payLocked ? undefined : `In steps of ${hoursIncrement}.`}
                  error={gaps.hours ?? undefined}
                >
                  <input
                    id={hoursId}
                    ref={hoursRef}
                    type="text"
                    inputMode="decimal"
                    value={hours}
                    disabled={payLocked}
                    aria-required="true"
                    aria-invalid={gaps.hours !== null ? true : undefined}
                    aria-describedby={describedBy(hoursId, !payLocked, gaps.hours !== null)}
                    onChange={(e) => {
                      setHours(e.target.value);
                      setGaps((g) => (g.hours === null ? g : { ...g, hours: null }));
                    }}
                    className={`w-full ${input} disabled:cursor-not-allowed disabled:opacity-50`}
                    data-testid={`prebilling-input-hours-${note.noteId}`}
                  />
                </Field>
                <Field label="Rate" htmlFor={bandId}>
                  <SelectField size="compact"
                    id={bandId}
                    value={rateBand}
                    disabled={payLocked}
                    onChange={(e) => setRateBand(e.target.value)}
                    className="w-full"
                    data-testid={`prebilling-input-band-${note.noteId}`}
                  >
                    <option value="">None</option>
                    {bandChoices.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </SelectField>
                </Field>
                {/* Time of day: a radiogroup is not labelable, so its label
                    is a span the group names itself by. The control fills
                    its field below `sm` and is content-sized from `sm`. No
                    after-hours admin: the field is absent while admin is
                    selected. */}
                {!noAfterHours && (
                  <div data-testid={`prebilling-field-afterhours-${note.noteId}`}>
                    <span id={timeOfDayId} className={FLD_LABEL}>
                      Time of day
                    </span>
                    <AfterHoursToggle
                      value={afterHours}
                      onChange={setAfterHours}
                      labelledBy={timeOfDayId}
                      disabled={payLocked}
                      testIdPrefix={`prebilling-input-afterhours-${note.noteId}`}
                    />
                  </div>
                )}
              </>
            )}
            <div className="col-span-full">
              <Field
                label="Invoice line"
                required={lineRequired}
                htmlFor={lineId}
                help="What the family reads on the invoice."
                error={gaps.line ?? undefined}
              >
                {/* A plain textarea: the line is stored as typed. */}
                <textarea
                  id={lineId}
                  ref={lineRef}
                  value={summary}
                  rows={3}
                  aria-required={lineRequired ? 'true' : undefined}
                  aria-invalid={gaps.line !== null ? true : undefined}
                  // A locked note's sentence is read with the line, the box
                  // focus lands in, since the four controls are skipped.
                  aria-describedby={[payLocked ? `prebilling-${note.noteId}-pay-locked` : null, describedBy(lineId, true, gaps.line !== null)]
                    .filter((v): v is string => typeof v === 'string' && v !== '')
                    .join(' ')}
                  onChange={(e) => {
                    setSummary(e.target.value);
                    setGaps((g) => (g.line === null ? g : { ...g, line: null }));
                  }}
                  className={`w-full ${input}`}
                  data-testid={`prebilling-input-summary-${note.noteId}`}
                />
              </Field>
            </div>
          </div>
          {/* Which half failed, honestly (partial saves are named, never
              papered over), above the buttons. */}
          {problemLine !== null && (
            <p
              role="alert"
              className="text-xs text-pepper whitespace-normal"
              data-testid={`prebilling-edit-problem-${note.noteId}`}
            >
              {problemLine}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              loading={saving}
              data-testid={`prebilling-save-${note.noteId}`}
            >
              {lineOnly || payLocked ? 'Save invoice line' : 'Save billing line'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={saving}
              data-testid={`prebilling-cancel-${note.noteId}`}
            >
              Cancel
            </Button>
            {/* The Notes tab's Remove, here too: a note logged by mistake
                (training on a practice client) comes off from where it is
                seen. Apart from Save, on the right. */}
            {canRemove && !note.payPeriodClosed && (
              <DestructiveTextAction
                className="ml-auto"
                onClick={() => setConfirmingRemove(true)}
                disabled={saving || removing}
                aria-busy={removing || undefined}
                data-testid={`prebilling-remove-${note.noteId}`}
              >
                Remove note
              </DestructiveTextAction>
            )}
          </div>
          {removeProblem !== null && (
            <p
              role="alert"
              className="text-xs text-pepper whitespace-normal"
              data-testid={`prebilling-remove-problem-${note.noteId}`}
            >
              {removeProblem}
            </p>
          )}
          {confirmingRemove && (
            <ConfirmDialog
              title="Remove this note?"
              body="It comes off Pre-billing and My time."
              confirmLabel="Remove note"
              destructive
              onConfirm={() => {
                setConfirmingRemove(false);
                onRemove();
              }}
              onCancel={() => setConfirmingRemove(false)}
            />
          )}
        </div>
  );
  if (layout === 'card') return box;
  return (
    <tr data-testid={`prebilling-edit-row-${note.noteId}`}>
      <td colSpan={columnCount} className="px-0 pt-0 pb-3">
        {box}
      </td>
    </tr>
  );
}

export interface PreBillingProps {
  clientId: string;
  /** For the CSV's Client column + filename (already on the page). */
  clientName: string;
  /** The page-injected local day ('YYYY-MM-DD') — defaults the picker
   *  to the current month deterministically. */
  today: string;
  /** Optional month deep-link ('YYYY-MM' — e.g. an invoice draft's
   *  edit-at-source link lands on the invoice's month). Clamped to the
   *  current month; malformed/future values fall back to it. */
  initialMonth?: string;
}

export function PreBilling({ clientId, clientName, today, initialMonth }: PreBillingProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const canEdit = canWriteClinicalNotes(whoami);
  // The one seat test on this screen: a seat holding view_invoice gets
  // the office screen; any other seat gets the lines with no money.
  const officeSeat = canViewInvoice(whoami);
  // The office's backstop: a seat that can mark any row (the note author
  // rule widened by moderate_clinical_notes) when a care manager is away.
  const currentMonth = today.slice(0, 7);
  const [month, setMonth] = useState(
    initialMonth !== undefined && /^\d{4}-(0[1-9]|1[0-2])$/.test(initialMonth) && initialMonth <= currentMonth
      ? initialMonth
      : currentMonth,
  );
  const { rows, nativeFrom, isLoading, isError, error, refetch } = usePrebilling(
    tenantSlug,
    clientId,
    month,
  );
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const bands = readRateBands(tenantConfig?.config);
  const bandLabels = bandLabelsFrom(bands);
  // 0134 rate-band matrix: the inline editor's band CHOICES are scoped to
  // the actor (whoami.role + does_spice) — display labels stay on the full
  // vocabulary. The server enforces the same set on the write path.
  const allowedBands = bandOptionsForWhoami(whoami, tenantConfig?.config, bands);

  const queryClient = useQueryClient();
  // A billing write here changes what the invoice pages show (the
  // unbilled report, the list, a draft's note count), so it refreshes
  // them with this tab.
  // The month a change was made in travels with the change (the mutation's
  // variables), so the read it refreshes is that month's even when the
  // person has moved to another.
  const invalidate = (forMonth: string): void => {
    void queryClient.invalidateQueries({ queryKey: prebillingKey(tenantSlug, clientId, forMonth) });
    invalidateInvoiceReads(queryClient, tenantSlug);
  };

  // One row at a time has its edit box open (one combined editor: the
  // billing fields and the invoice line, one Save).
  const [editingRow, setEditingRow] = useState<string | null>(null);
  // The row whose box just closed by Cancel or a successful save: its Edit
  // button, which mounts again with the close, takes focus back. Another
  // row opening its box is not a close, so it never sets this.
  const returnFocusTo = useRef<string | null>(null);
  const onFocusReturned = useCallback((): void => {
    returnFocusTo.current = null;
  }, []);

  // The combined Save's failure, named per half (see CombinedSaveError),
  // with a refused date kept apart for the date box.
  const [editProblem, setEditProblem] = useState<({ noteId: string } & EditProblem) | null>(null);
  // The save's own sentence when an office edit left the note billed by
  // export (editLeftInvoiceSentence), under that row until the next save.
  const [editNotice, setEditNotice] = useState<{ noteId: string; message: string } | null>(null);
  const editMut = useMutation<unknown, unknown, { noteId: string; patch: BillingPatch; summary?: string; month: string }>({
    // The two halves ride two endpoints ON PURPOSE (the billing PATCH's
    // allowlist and the summary PATCH's are disjoint —
    // validators/encounterNotes.ts). Line first, then billing; a partial
    // failure is surfaced honestly, never papered over.
    mutationFn: ({ noteId, patch, summary }) => saveBillingLine(tenantSlug, noteId, patch, summary),
    onMutate: () => {
      setEditProblem(null);
      setEditNotice(null);
    },
    onSuccess: (res, { noteId, month: forMonth }) => {
      returnFocusTo.current = noteId;
      setEditingRow(null);
      const message = editLeftInvoiceSentence(res);
      setEditNotice(message === null ? null : { noteId, message });
      invalidate(forMonth);
      // patchNoteBilling can change hours, date, band or after-hours: any
      // of those can move what My time shows for this note's author.
      void invalidateMyHours(queryClient, tenantSlug);
    },
    onError: (err, { noteId, month: forMonth }) => {
      setEditProblem({ noteId, ...editProblemFor(err, contact) });
      // The line half landed — refetch so the row beneath the open
      // editor tells the truth about what saved.
      if (err instanceof CombinedSaveError && err.lineSaved) invalidate(forMonth);
    },
  });
  // A seat without view_invoice saves the invoice line alone, through the
  // summary route: the route's author rule is the server's own check. Its
  // refusal carries the server's sentence (the author rule, the invoice
  // freeze), which combinedSaveMessage shows as sent.
  const lineMut = useMutation<unknown, unknown, { noteId: string; summary: string; month: string }>({
    mutationFn: async ({ noteId, summary }) => {
      try {
        await patchNoteSummary(tenantSlug, noteId, summary);
      } catch (err) {
        throw new CombinedSaveError('line', false, err);
      }
      return null;
    },
    onMutate: () => {
      setEditProblem(null);
    },
    onSuccess: (_res, { noteId, month: forMonth }) => {
      returnFocusTo.current = noteId;
      setEditingRow(null);
      invalidate(forMonth);
    },
    onError: (err, { noteId }) => {
      setEditProblem({ noteId, ...editProblemFor(err, contact) });
    },
  });
  // Remove note (the Notes tab's remove): the note leaves this list, My
  // time, the invoice pages and the client's Notes feed, so each reads
  // again. A refusal (a note on an approved or issued invoice) says the
  // server's sentence in the box.
  const [removeProblem, setRemoveProblem] = useState<{ noteId: string; message: string } | null>(null);
  const removeMut = useMutation<unknown, unknown, { noteId: string; month: string }>({
    mutationFn: ({ noteId }) => notesApi.removeNote(tenantSlug, noteId),
    onMutate: () => {
      setRemoveProblem(null);
    },
    onSuccess: (_res, { month: forMonth }) => {
      setEditingRow(null);
      setEditProblem(null);
      invalidate(forMonth);
      void invalidateMyHours(queryClient, tenantSlug);
      void invalidateMyPrebilling(queryClient, tenantSlug);
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
    },
    onError: (err, { noteId }) => {
      setRemoveProblem({ noteId, message: removeErrorMessage(err, contact) });
    },
  });
  // Shadow rates (founder feedback 2026-07-10, B1 live): the same
  // client-rates read the Rates card above already makes (shared query
  // key — one fetch per tab). Each row shows its band's current rate as
  // quiet text, or "No rate on file" in the attention register BEFORE
  // anyone sets a row Ready — the blocker is visible, never a
  // surprise refusal.
  const { rates } = useClientRates(tenantSlug, clientId, { enabled: officeSeat });

  // The mark-ready refusals worth naming: 0135's fail-closed rate gate,
  // with the fix on this tab; and any refusal that carries the server's
  // own sentence (the note author rule, the invoice freeze), shown as
  // sent. Everything else keeps the mutation's default (quiet) behavior.
  const [readyProblem, setReadyProblem] = useState<{ noteId: string; message: string } | null>(
    null,
  );
  // Native billing OFF for the workspace: the wire's native_from is null
  // once the month has loaded (the server reader nulls an unset AND a
  // malformed key). Tenant-wide, permanent until an owner writes the
  // key, so it renders ONCE at the head of the tab and turns the footer
  // honest. A note dated before a SET boundary is the other, ambient
  // state (PrebillingNote.beforeNativeStart), never this one.
  const nativeBillingOff = officeSeat && rows !== null && nativeFrom === null;

  // The value each row's switch shows while its request is in flight, by
  // note: the chosen value shows at once and stays until the refetch has
  // landed, so the switch never flickers back.
  const [pendingReady, setPendingReady] = useState<Record<string, boolean>>({});
  const readyMut = useMutation<unknown, unknown, { noteId: string; ready: boolean; month: string }>({
    mutationFn: ({ noteId, ready }) => setNoteBillingReady(tenantSlug, noteId, ready),
    onMutate: ({ noteId, ready }) => {
      setReadyProblem(null);
      setPendingReady((p) => ({ ...p, [noteId]: ready }));
    },
    onSuccess: async (_res, { month: forMonth }) => {
      invalidateInvoiceReads(queryClient, tenantSlug);
      await queryClient.invalidateQueries({ queryKey: prebillingKey(tenantSlug, clientId, forMonth) });
    },
    onSettled: (_res, _err, { noteId }) => {
      setPendingReady((p) => {
        const next = { ...p };
        delete next[noteId];
        return next;
      });
    },
    onError: (err, { noteId }) => {
      setReadyProblem({ noteId, message: readyRefusalMessage(err, officeSeat, contact) });
    },
  });

  const ready = rows === null ? [] : readyRowsOf(rows);
  const bandTotals = rows === null ? [] : readyHoursByBand(rows, bands);
  // The table's columns by seat: the office reads the rate band; a seat
  // without view_invoice does not (their After Hours mark sits beside the
  // hours). The header draws this list and an open edit box spans it.
  const columns = officeSeat
    ? ['Date', 'Hours', 'Rate', 'Summary', 'Status', 'Edit']
    : ['Date', 'Hours', 'Summary', 'Status', 'Edit'];
  // The columns are fixed, so marking a row never re-flows them. Each has
  // its widest content's width; Summary takes the rest of the table, and
  // the table's floor keeps at least 16rem for it (below the floor the
  // wrapper scrolls sideways).
  const columnWidths: Record<string, string | undefined> = {
    Date: 'w-[9rem]',
    Hours: officeSeat ? 'w-[5.5rem]' : 'w-[10rem]',
    Rate: 'w-[13.5rem]',
    Summary: undefined,
    Status: STATUS_COLUMN_WIDTH,
    Edit: EDIT_COLUMN_WIDTH,
  };
  const tableFloor = officeSeat ? 'min-w-[71.5rem]' : 'min-w-[62.5rem]';
  // A row's doors. The office's follow the note author rule: its author,
  // or a seat that moderates notes, for Edit and the Ready switch alike;
  // the moderating seats are the backstop. A seat without view_invoice
  // edits the line on, and sets Ready on, only a note they wrote, whatever
  // else they hold. The bulk button counts and marks only the awaiting
  // rows this viewer may mark, less any already on their way to Ready.
  const isOwn = (r: PrebillingNote): boolean => whoami !== null && r.authorUserId === whoami.user_id;
  const mayEditLine = (r: PrebillingNote): boolean =>
    officeSeat ? canEdit && canChangeNote(whoami, r.authorUserId) : canEdit && isOwn(r);
  const mayMark = (r: PrebillingNote): boolean =>
    officeSeat ? canEdit && canChangeNote(whoami, r.authorUserId) : canEdit && isOwn(r);
  const eligibleUnconfirmed =
    rows === null
      ? []
      : rows.filter((r) => r.readiness === 'needs_your_ok' && mayMark(r) && pendingReady[r.noteId] !== true);
  // Once the bulk button has shown for a month it stays, at (0) and
  // disabled, when nothing is left for it: the row above the table keeps
  // its height, so marking the last note never moves the table.
  const [bulkShownFor, setBulkShownFor] = useState<string | null>(null);
  useEffect(() => {
    if (canEdit && eligibleUnconfirmed.length > 0 && bulkShownFor !== month) setBulkShownFor(month);
  }, [canEdit, eligibleUnconfirmed.length, bulkShownFor, month]);
  const showBulk = canEdit && (eligibleUnconfirmed.length > 0 || bulkShownFor === month);
  const readyInFlight = Object.keys(pendingReady).length > 0;

  function downloadCsv(): void {
    if (rows === null || ready.length === 0) return;
    downloadCsvFile(
      prebillingCsvFilename(clientName, month),
      buildPrebillingCsv(clientName, rows, bands),
    );
  }

  function markAll(): void {
    for (const r of eligibleUnconfirmed) {
      readyMut.mutate({ noteId: r.noteId, ready: true, month });
    }
  }

  return (
    <div className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-6">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-display text-xl text-sage-800">Pre-billing</h3>
        <div className="flex flex-wrap items-center gap-4">
          {/* The pair of InvoicePreview's back-link (founder feedback
              2026-07-10): billing and invoicing point at each other. */}
          {officeSeat && (
            <Link
              to={`/t/${tenantSlug}/invoices`}
              data-testid="prebilling-go-invoicing"
              className="inline-flex items-center gap-[7px] rounded text-[13.5px] font-semibold text-ink-soft transition-colors duration-[var(--dur-base)] ease-quiet hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              Go to invoicing
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          )}
          <div className="flex items-center gap-1" data-testid="prebilling-month-picker">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Previous month"
            data-testid="prebilling-month-prev"
            onClick={() => setMonth(prevMonth(month))}
          >
            ‹
          </Button>
          <span
            className="min-w-[8.5rem] text-center text-sm font-semibold text-sage-800"
            data-testid="prebilling-month-label"
          >
            {monthLabel(month)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Next month"
            data-testid="prebilling-month-next"
            disabled={month >= currentMonth}
            onClick={() => setMonth(nextMonth(month))}
          >
            ›
          </Button>
          </div>
        </div>
      </header>
      <UnpostedDraftsWarning clientId={clientId} className="mt-3 mb-3" />

      {nativeBillingOff && <NativeBillingOffBanner testId="prebilling-native-off-banner" />}

      {/* One row above the table: on the left the quiet "What the states
          mean" link (the retired Admin "Billing rules" page's substance,
          where the states appear; founder feedback 2026-07-10), and on the
          right the page's actions, never below the list: Mark all ready
          (the primary), then Download CSV. */}
      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="prebilling-toolbar">
      <PrebillingStatesLegend officeSeat={officeSeat} />
      {(showBulk || officeSeat) && (
        <div className="ml-auto flex flex-wrap items-center justify-end gap-3" data-testid="prebilling-actions">
          {showBulk && (
            <Button
              variant="primary"
              size="sm"
              onClick={markAll}
              disabled={eligibleUnconfirmed.length === 0 || readyInFlight}
              data-testid="prebilling-mark-all"
            >
              {officeSeat ? 'Mark all ready' : 'Mark my notes ready'} ({eligibleUnconfirmed.length})
            </Button>
          )}
          {officeSeat && (
            <Button
              variant="secondary"
              size="sm"
              disabled={ready.length === 0}
              onClick={downloadCsv}
              data-testid="prebilling-download"
            >
              Download CSV
            </Button>
          )}
        </div>
      )}
      </div>

      <AsyncBoundary what="pre-billing"
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        isEmpty={rows !== null && rows.length === 0}
        emptyHeadline={`Nothing to bill for ${monthName(month)} yet`}
        emptyBody="Post a case note and you'll see its billing lines here."
      >
        {rows !== null && rows.length > 0 && (
          <>
            {/* The wrapper is the table's scroll area and an inline-size
                container: an open edit box is as wide as the wrapper's
                visible area (100cqw) and sticks to its left edge while the
                table scrolls sideways. The table stays its only child. It
                is positioned, so a screen-reader-only label in the table
                (the Edit header) is clipped with the table and never widens
                the page. */}
            <div className="relative overflow-x-auto @container" data-testid="prebilling-table-scroll">
              <table className={`w-full table-fixed text-sm ${tableFloor}`} data-testid="prebilling-table">
                <colgroup>
                  {columns.map((column) => (
                    <col key={column} className={columnWidths[column]} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-400">
                    {columns.map((column, i) => (
                      <th key={column} className={i === columns.length - 1 ? 'py-1.5 font-semibold' : 'py-1.5 pr-3 font-semibold'}>
                        {columnHeading(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-sage-800/[6%]">
                  {rows.map((note) => (
                    <PrebillingRow
                      key={note.noteId}
                      note={note}
                      bands={bands}
                      allowedBands={allowedBands}
                      bandLabels={bandLabels}
                      officeSeat={officeSeat}
                      columnCount={columns.length}
                      currentRate={
                        note.rateBand === null
                          ? null
                          : (bandRatesOf(rates ?? [], note.rateBand, today).current?.rate ?? null)
                      }
                      nativeFrom={nativeFrom}
                      readyProblem={readyProblem?.noteId === note.noteId ? readyProblem.message : null}
                      editNotice={editNotice?.noteId === note.noteId ? editNotice.message : null}
                      editProblem={
                        editProblem?.noteId === note.noteId
                          ? { message: editProblem.message, date: editProblem.date }
                          : null
                      }
                      viewerIsAuthor={isOwn(note)}
                      viewerUserId={whoami?.user_id ?? null}
                      canEditLine={mayEditLine(note)}
                      canMark={mayMark(note)}
                      isEditing={editingRow === note.noteId}
                      returnFocus={returnFocusTo.current === note.noteId}
                      onFocusReturned={onFocusReturned}
                      onStartEdit={() => {
                        setEditProblem(null);
                        setRemoveProblem(null);
                        setEditingRow(note.noteId);
                      }}
                      onCancel={() => {
                        returnFocusTo.current = note.noteId;
                        setEditProblem(null);
                        setRemoveProblem(null);
                        setEditingRow(null);
                      }}
                      onDateChanged={() =>
                        setEditProblem((p) => (p === null || p.date === null ? p : { ...p, date: null }))
                      }
                      onSave={(patch, summary) =>
                        editMut.mutate({ noteId: note.noteId, patch, summary, month })
                      }
                      onSaveLine={(summary) => lineMut.mutate({ noteId: note.noteId, summary, month })}
                      readyPending={pendingReady[note.noteId]}
                      onSetReady={(r) => readyMut.mutate({ noteId: note.noteId, ready: r, month })}
                      saving={(editMut.isPending || lineMut.isPending) && editingRow === note.noteId}
                      canRemove={canChangeNote(whoami, note.authorUserId)}
                      onRemove={() => removeMut.mutate({ noteId: note.noteId, month })}
                      removing={removeMut.isPending && removeMut.variables?.noteId === note.noteId}
                      removeProblem={removeProblem?.noteId === note.noteId ? removeProblem.message : null}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer: total READY hours by rate band — what the CSV carries.
                With native billing off nothing here will bill, so the
                label says what is true: these hours export. */}
            <div
              className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-sage-800/[8%] pt-3 text-sm"
              data-testid="prebilling-footer"
            >
              <span className="font-semibold text-sage-800">
                {!officeSeat ? 'Ready' : nativeBillingOff ? 'Ready to export' : 'Ready to bill'}
              </span>
              {bandTotals.length > 0 ? (
                bandTotals.map((t) => (
                  <span key={t.band} className="text-ink-soft">
                    {t.label} · <span className="text-sage-800">{formatHours(t.hours)}</span>
                  </span>
                ))
              ) : (
                <span className="italic text-ink-soft">Nothing yet.</span>
              )}
            </div>
          </>
        )}
      </AsyncBoundary>

    </div>
  );
}
